import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Restore as RestoreIcon,
  Visibility as ViewIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CompareArrows as CompareIcon,
  TrendingUp as ImproveIcon,
  TrendingDown as DeclineIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';

// Simple date formatting function to avoid date-fns dependency
const format = (date: Date, formatStr: string) => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

interface OptimizationRecord {
  id: string;
  timestamp: Date;
  version: number;
  content: string;
  cqcScore: number;
  keywordDensity: number;
  entityDensity: number;
  wordCount: number;
  settings: any;
  improvement: number;
  isSaved: boolean;
  notes?: string;
}

interface OptimizationHistoryProps {
  sessionId: string;
  onRestore: (content: string) => void;
}

export const OptimizationHistory: React.FC<OptimizationHistoryProps> = ({
  sessionId,
  onRestore
}) => {
  const [history, setHistory] = useState<OptimizationRecord[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<OptimizationRecord | null>(null);
  const [compareVersions, setCompareVersions] = useState<[OptimizationRecord?, OptimizationRecord?]>([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchHistory();
  }, [sessionId]);

  const fetchHistory = async () => {
    try {
      const response = await backendApiService.get(`/api/content-optimizer/history/${sessionId}`);
      if (response.success && response.data) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleSaveVersion = async (record: OptimizationRecord) => {
    try {
      await backendApiService.post('/api/content-optimizer/history/save', {
        sessionId,
        versionId: record.id,
        notes
      });
      
      // Update local state
      setHistory(prev => prev.map(h =>
        h.id === record.id ? { ...h, isSaved: true, notes } : h
      ));
      setNotes('');
    } catch (error) {
      console.error('Error saving version:', error);
    }
  };

  const handleDeleteVersion = async (record: OptimizationRecord) => {
    if (!confirm('Are you sure you want to delete this version?')) return;
    
    try {
      await backendApiService.delete(`/api/content-optimizer/history/${record.id}`);
      setHistory(prev => prev.filter(h => h.id !== record.id));
    } catch (error) {
      console.error('Error deleting version:', error);
    }
  };

  const handleCompare = (v1: OptimizationRecord, v2: OptimizationRecord) => {
    setCompareVersions([v1, v2]);
    setCompareDialogOpen(true);
  };

  const getImprovementColor = (improvement: number): 'success' | 'info' | 'error' | 'warning' => {
    if (improvement > 10) return 'success';
    if (improvement > 0) return 'info';
    if (improvement === 0) return 'warning';
    return 'error';
  };

  const getImprovementIcon = (improvement: number) => {
    if (improvement > 0) return <ImproveIcon fontSize="small" />;
    if (improvement < 0) return <DeclineIcon fontSize="small" />;
    return <></>;  // Return empty fragment instead of null
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Optimization History
      </Typography>

      {/* Timeline View */}
      <Paper sx={{ p: 2, mb: 3, maxHeight: 400, overflow: 'auto' }}>
        <Timeline position="alternate">
          {history.map((record, index) => (
            <TimelineItem key={record.id}>
              <TimelineOppositeContent color="textSecondary">
                {format(new Date(record.timestamp), 'HH:mm:ss')}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineDot
                  color={getImprovementColor(record.improvement)}
                  variant={record.isSaved ? 'filled' : 'outlined'}
                >
                  {record.isSaved ? <StarIcon /> : getImprovementIcon(record.improvement)}
                </TimelineDot>
                {index < history.length - 1 && <TimelineConnector />}
              </TimelineSeparator>
              <TimelineContent>
                <Paper sx={{ p: 2, cursor: 'pointer' }} onClick={() => setSelectedVersion(record)}>
                  <Typography variant="subtitle2">
                    Version {record.version}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={`CQC: ${record.cqcScore}%`}
                      size="small"
                      color={record.cqcScore >= 80 ? 'success' : 'warning'}
                    />
                    {record.improvement !== 0 && (
                      <Chip
                        label={`${record.improvement > 0 ? '+' : ''}${record.improvement}%`}
                        size="small"
                        color={getImprovementColor(record.improvement)}
                        icon={record.improvement !== 0 ? getImprovementIcon(record.improvement) : undefined}
                      />
                    )}
                  </Box>
                  {record.notes && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {record.notes}
                    </Typography>
                  )}
                </Paper>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Paper>

      {/* Table View */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Time</TableCell>
              <TableCell align="center">CQC Score</TableCell>
              <TableCell align="center">Keyword Density</TableCell>
              <TableCell align="center">Entity Density</TableCell>
              <TableCell align="center">Words</TableCell>
              <TableCell align="center">Improvement</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((record, index) => (
              <TableRow
                key={record.id}
                sx={{
                  bgcolor: record.isSaved ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {record.isSaved && <StarIcon fontSize="small" color="primary" />}
                    Version {record.version}
                  </Box>
                </TableCell>
                <TableCell>{format(new Date(record.timestamp), 'HH:mm:ss')}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={`${record.cqcScore}%`}
                    size="small"
                    color={record.cqcScore >= 80 ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell align="center">{record.keywordDensity.toFixed(1)}%</TableCell>
                <TableCell align="center">{record.entityDensity.toFixed(1)}</TableCell>
                <TableCell align="center">{record.wordCount}</TableCell>
                <TableCell align="center">
                  {record.improvement !== 0 && (
                    <Chip
                      label={`${record.improvement > 0 ? '+' : ''}${record.improvement}%`}
                      size="small"
                      color={getImprovementColor(record.improvement)}
                      icon={getImprovementIcon(record.improvement)}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View content">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedVersion(record);
                          setViewDialogOpen(true);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Restore this version">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => onRestore(record.content)}
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {index > 0 && (
                      <Tooltip title="Compare with previous">
                        <IconButton
                          size="small"
                          onClick={() => handleCompare(history[index - 1], record)}
                        >
                          <CompareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={record.isSaved ? "Saved" : "Save this version"}>
                      <IconButton
                        size="small"
                        onClick={() => handleSaveVersion(record)}
                        color={record.isSaved ? "default" : "primary"}
                      >
                        {record.isSaved ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {!record.isSaved && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteVersion(record)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Best Version Highlight */}
      {history.length > 0 && (
        <Paper sx={{ p: 2, mt: 3, bgcolor: 'success.light' }}>
          <Typography variant="subtitle2" gutterBottom>
            🏆 Best Performing Version
          </Typography>
          {(() => {
            const best = history.reduce((prev, current) =>
              current.cqcScore > prev.cqcScore ? current : prev
            );
            return (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2">
                    Version {best.version} - CQC Score: {best.cqcScore}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Created at {format(new Date(best.timestamp), 'HH:mm:ss')}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<RestoreIcon />}
                  onClick={() => onRestore(best.content)}
                >
                  Restore Best
                </Button>
              </Box>
            );
          })()}
        </Paper>
      )}

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Version {selectedVersion?.version} Content
        </DialogTitle>
        <DialogContent>
          {selectedVersion && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Chip label={`CQC: ${selectedVersion.cqcScore}%`} />
                <Chip label={`Keywords: ${selectedVersion.keywordDensity.toFixed(1)}%`} />
                <Chip label={`Entities: ${selectedVersion.entityDensity.toFixed(1)}`} />
                <Chip label={`${selectedVersion.wordCount} words`} />
              </Box>
              <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedVersion.content}
                </Typography>
              </Paper>
              {!selectedVersion.isSaved && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Version Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={2}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedVersion && !selectedVersion.isSaved && (
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => {
                handleSaveVersion(selectedVersion);
                setViewDialogOpen(false);
              }}
            >
              Save Version
            </Button>
          )}
          {selectedVersion && (
            <Button
              variant="contained"
              startIcon={<RestoreIcon />}
              onClick={() => {
                onRestore(selectedVersion.content);
                setViewDialogOpen(false);
              }}
            >
              Restore
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Compare Versions {compareVersions[0]?.version} and {compareVersions[1]?.version}
        </DialogTitle>
        <DialogContent>
          {compareVersions[0] && compareVersions[1] && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Version {compareVersions[0].version}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip label={`CQC: ${compareVersions[0].cqcScore}%`} size="small" />
                    <Chip label={`${compareVersions[0].wordCount} words`} size="small" />
                  </Box>
                  <Typography variant="body2" sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {compareVersions[0].content.substring(0, 500)}...
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Version {compareVersions[1].version}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip label={`CQC: ${compareVersions[1].cqcScore}%`} size="small" />
                    <Chip label={`${compareVersions[1].wordCount} words`} size="small" />
                  </Box>
                  <Typography variant="body2" sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {compareVersions[1].content.substring(0, 500)}...
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};